import { useEffect, useState } from 'react';
import { useLoaderData } from 'react-router';
import { Calendar, Plus } from 'lucide-react';

import {
    Carousel,
    CarouselContent,
    CarouselItem,
    type CarouselApi,
} from '@rocco/components/carousel';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import {
    DetailScrollReveal,
    TeamMemberOverlay,
    useIslandAutoplay,
} from '~/ui-rocco/pages/detail-shared';
import type { TeamMember } from '~/ui-rocco/pages/detail-shared';
import { button } from '~/ui-rocco/button-system';
import {
    useBookMeetingGateEnabled,
    useOpenBookMeetingGate,
} from '~/modules-rocco/booking/book-meeting-gate-state';

/**
 * One team-member slide — the TeamGrid tile (portrait, name, role), with a
 * Book a meeting control resting on the portrait.
 *
 * The member is offered by name because this section only renders people the
 * professional has made public, so the reader is choosing a face rather than
 * being handed a roster to pick from afterwards. See BC-5174.
 */
const TeamMemberSlide = ({
    member,
    onBookMeeting,
    onOpen,
}: {
    member: TeamMember;
    onBookMeeting?: (member: TeamMember) => void;
    onOpen: (member: TeamMember) => void;
}) => (
    <div className="flex flex-col">
        <div className="bg-neutral-200 relative aspect-[437/524] w-full overflow-hidden rounded-tile">
            {member.image ? (
                <img
                    src={member.image}
                    alt={member.name}
                    loading="lazy"
                    className="absolute inset-0 size-full object-cover"
                />
            ) : null}
            {onBookMeeting ? (
                <button
                    type="button"
                    onClick={() => onBookMeeting(member)}
                    // Named here because the label is hidden on a phone.
                    aria-label="Book a meeting"
                    // The platform button rather than its own pill: this rests
                    // on a photograph beside Save and Visit site, which are the
                    // lifted family, and it was reading as a smaller control
                    // from an older page. Position only through `extra` — the
                    // height, corner and padding belong to the size step.
                    //
                    // The tile-control step: a square on the phone, where the
                    // portrait is 176px wide and the labelled L ran 199px, out
                    // past the picture it sits on. It grows back to the L with
                    // its label from `md`, where there is room for one.
                    className={button(
                        'lifted',
                        ['m', 'l'],
                        'absolute bottom-4 left-4',
                        { icon: 'max-md' },
                    )}
                >
                    {/* Plain calendar, not CalendarPlus: this tile leads to a
                        booking for a member with a connected calendar and to a
                        preferred-time request for everyone else, so the icon
                        cannot promise something lands in a diary. */}
                    <Calendar
                        aria-hidden="true"
                        className="size-[1.125rem] shrink-0"
                    />
                    <span className="max-md:hidden">Book a meeting</span>
                </button>
            ) : null}
            {/* The project page's pair: the booking on the left, the person on
                the right, both square at the phone step. */}
            <button
                type="button"
                onClick={() => onOpen(member)}
                aria-label={`About ${member.name}`}
                className={button(
                    'lifted',
                    ['m', 'l'],
                    'absolute right-4 bottom-4',
                    { icon: true },
                )}
            >
                <Plus aria-hidden="true" className="size-[1.125rem] shrink-0" />
            </button>
        </div>
        <p className="m-0 font-sans leading-snug font-medium text-[#181818] max-md:pt-3 max-md:text-base md:pt-[0.625rem] md:text-[0.875rem]">
            {member.name}
        </p>
        {member.jobTitle ? (
            <p className="m-0 font-sans leading-snug text-[#181818]/40 max-md:text-sm md:text-[0.875rem]">
                {member.jobTitle}
            </p>
        ) : null}
    </div>
);

/**
 * §6 — Team. The numbered section label over the professional's public team
 * members as a
 * four-column full-bleed carousel with Magic Island pagination, mirroring the
 * product page's About-the-Supplier products rail. The full team grid lives on
 * the About (/info) tab's Our Team accordion; this section is the teaser.
 * Self-hides when there are no members.
 */
export const ProfessionalTeamSection = () => {
    const { teamMembers, professionalOverview } =
        useLoaderData<typeof loader>();
    // The branch list lives on the page's initial data, not the overview's.
    const initial = useProfessionalInitialRouteData();
    const bookMeetingEnabled = useBookMeetingGateEnabled();
    const openBookMeetingGate = useOpenBookMeetingGate();

    const onBookMeeting = bookMeetingEnabled
        ? (member: TeamMember) =>
              openBookMeetingGate({
                  professionalUrlSegment: professionalOverview.URLSegment,
                  siteTreeId: professionalOverview.ID,
                  // No itemTitle: a profile has no subject of its own, and passing the title made the request read as being about the practice you are writing to.
                  preferredTeamMemberId: member.id,
                  // The tile already has both, so the pane opens on this
                  // person rather than resolving its way to them.
                  preferredTeamMemberName: member.name,
                  paneImage: member.image || undefined,
                  // This section renders the roster, so it can tell the modal
                  // whether a picker is coming before the fetch answers. One
                  // public member still gets a field, because "anyone" is the
                  // other option in it.
                  expectsTeamPicker: teamMembers.length > 0,
                  professionalName: professionalOverview.Title,
                  companyLogo: professionalOverview.CompanyLogo,
                  logoBackgroundColor: professionalOverview.LogoBackgroundColor,
              })
        : undefined;

    const [openMember, setOpenMember] = useState<TeamMember | null>(null);
    const [emblaApi, setEmblaApi] = useState<CarouselApi | null>(null);
    // Gate the island on the scroll-reveal so its pagination cannot show (or
    // auto-advance) while the slides are still faded out.
    const [revealed, setRevealed] = useState(false);
    // ...and on the carousel actually having somewhere to scroll: at four
    // columns a small team fits in one viewport (a single snap), where island
    // pagination would show one dot that immediately reads as ended.
    const [scrollable, setScrollable] = useState(false);
    useEffect(() => {
        if (!emblaApi) return;
        const update = () =>
            setScrollable(emblaApi.scrollSnapList().length > 1);
        update();
        emblaApi.on('reInit', update);
        return () => {
            emblaApi.off('reInit', update);
        };
    }, [emblaApi]);
    const islandRef = useIslandAutoplay<HTMLElement>(emblaApi, {
        label: 'Team members',
        enabled: scrollable && revealed,
    });

    if (!teamMembers || teamMembers.length === 0) return null;

    return (
        <section ref={islandRef} className="overflow-hidden bg-ground">
            {/* Named, not numbered, in the statement's own voice — the same
                heading Who we work with carries below it. */}
            <div className="mx-auto w-full max-w-(--ap-page-width) max-md:px-6 max-md:pt-5 md:px-[3rem] md:pt-[1.875rem]">
                <h2 className="m-0 font-sans font-medium tracking-[-0.025em] text-black max-md:text-[2rem] max-md:leading-[1.08] md:text-[3rem] md:leading-[1.05]">
                    Who we are
                </h2>
            </div>
            {/* animate={false}: the overview page already fades the whole
                section in — a second animated reveal here left the slides at
                opacity zero under an already-visible header. The observer (and
                the island gate it drives) still fires on schedule. */}
            <DetailScrollReveal
                delay={120}
                animate={false}
                onReveal={() => setRevealed(true)}
                className="w-full max-md:pt-8 md:pt-[3rem]"
            >
                <Carousel
                    setApi={setEmblaApi}
                    ariaLabel="Team members"
                    options={{
                        align: 'start',
                        containScroll: 'trimSnaps',
                        dragFree: true,
                        slidesToScroll: 'auto',
                        breakpoints: {
                            '(min-width: 768px)': { slidesToScroll: 1 },
                        },
                    }}
                >
                    {/* Gutter padding on the viewport keeps the first/last snaps
                        aligned to the page gutter while the slides scroll
                        full-bleed to the viewport edge (the About-the-Supplier
                        treatment). Four columns between the gutters, with the
                        grid's 24px gap carried by per-item padding. */}
                    <CarouselContent
                        className="max-md:gap-3 md:-mx-[0.375rem]"
                        viewportClassName="max-md:px-4 md:px-[3rem] qhd:px-[calc((100%_-_120rem)/2_+_3rem)]"
                    >
                        {teamMembers.map((member) => (
                            <CarouselItem
                                key={member.id}
                                className="max-md:basis-[46%] md:basis-1/4 md:px-[0.375rem]"
                            >
                                <TeamMemberSlide
                                    member={member}
                                    onBookMeeting={onBookMeeting}
                                    onOpen={setOpenMember}
                                />
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>
            </DetailScrollReveal>
            {openMember ? (
                <TeamMemberOverlay
                    displayName={openMember.name || professionalOverview.Title}
                    // A profile lists people, but a record can still reach here
                    // without a name of its own — then the practice stands in,
                    // and it is one phrase rather than a first and last name.
                    isPerson={Boolean(openMember.name)}
                    jobTitle={openMember.jobTitle}
                    summary={openMember.summary}
                    thumbnail={openMember.image}
                    practice={{
                        id: professionalOverview.ID,
                        name: professionalOverview.Title,
                        link: professionalOverview.Link,
                        companyLogo: professionalOverview.CompanyLogo ?? '',
                        logoBackgroundColor:
                            professionalOverview.LogoBackgroundColor ?? '',
                        // The practice's own enquiry wording and branches, as
                        // every other CTA on this profile sends them. Left off,
                        // the one message started from a team member arrived
                        // with the generic default and no branch to address.
                        customEnquiryMessage:
                            professionalOverview.CustomEnquiryMessage ??
                            undefined,
                        enquiryMessageOverrideEnabled:
                            professionalOverview.EnquiryMessageOverrideEnabled ??
                            undefined,
                        enquiryMessagePlaceholder:
                            professionalOverview.EnquiryMessagePlaceholder ??
                            undefined,
                        branchOptions: initial?.branchOptions,
                    }}
                    subject={{
                        itemType: 'professional',
                        siteTreeID: professionalOverview.ID,
                    }}
                    onBookMeeting={
                        onBookMeeting
                            ? () => onBookMeeting(openMember)
                            : undefined
                    }
                    onClose={() => setOpenMember(null)}
                />
            ) : null}
        </section>
    );
};
